const YIELD_EVERY = 100;
const PROGRESS_EVERY = 25;

const nowTick = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });

const asArray = (value) => Array.from(value || []);

// FileList is a live browser object. Clearing an input can empty the same
// FileList instance, so always take a stable snapshot before resetting it to
// allow the user to select the same directory again.
export const consumeDirectoryInputFiles = (input) => {
  const files = asArray(input?.files);
  if (input) input.value = "";
  return files;
};

// randomUUID is unavailable in older browsers and in the jsdom version used by
// this application. Import entry ids only need to be unique within one scan;
// they are not persisted as security tokens.
const createClientEntryId = () => {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  return `entry-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const normalizeSeparators = (value = "") =>
  String(value || "")
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
    .replace(/^\/+/, "")
    .trim();

const isSystemArtifact = (relativePath = "") => {
  const pathText = normalizeSeparators(relativePath).toLowerCase();
  const segments = pathText.split("/").filter(Boolean);
  const fileName = segments[segments.length - 1] || "";
  if (segments.includes("__macosx")) return true;
  if (fileName === ".ds_store" || fileName === "thumbs.db" || fileName === "desktop.ini") return true;
  if (fileName.startsWith("._")) return true;
  return false;
};

const createScanContext = ({ signal, onProgress } = {}) => ({
  signal,
  onProgress,
  touched: 0,
  lastReportedTouch: 0,
  lastProgress: null,
  reportProgress() {
    if (this.touched === this.lastReportedTouch) return;
    this.lastReportedTouch = this.touched;
    if (typeof onProgress === "function") {
      onProgress(this.lastProgress);
    }
  },
  async checkpoint(extra = {}) {
    if (signal?.aborted) {
      throw new DOMException("Folder scan was cancelled.", "AbortError");
    }
    this.touched += 1;
    this.lastProgress = extra;
    // A folder picker can produce dozens of entries before the next browser
    // frame. Avoid enqueuing a React state update for every scanned file.
    if (this.touched === 1 || this.touched % PROGRESS_EVERY === 0) {
      this.reportProgress();
    }
    if (this.touched % YIELD_EVERY === 0) {
      await nowTick();
    }
  },
  flushProgress() {
    // Always report final totals, including scans shorter than a full batch.
    this.reportProgress();
  },
});

const createFolderEntry = (relativePath = "", rootName = "") => {
  const normalized = normalizeSeparators(relativePath || rootName);
  return {
    clientEntryId: createClientEntryId(),
    entryType: "folder",
    relativePath: normalized,
    parentRelativePath: normalized.split("/").slice(0, -1).join("/"),
    fileName: normalized.split("/").pop() || rootName || "Folder",
    size: 0,
    lastModified: null,
    mimeType: "application/x-directory",
    emptyFolder: true,
  };
};

const createFileEntry = ({ relativePath, file }) => ({
  clientEntryId: createClientEntryId(),
  entryType: "file",
  relativePath: normalizeSeparators(relativePath),
  parentRelativePath: normalizeSeparators(relativePath).split("/").slice(0, -1).join("/"),
  fileName: file?.name || normalizeSeparators(relativePath).split("/").pop() || "file",
  size: Number(file?.size || 0),
  lastModified: Number(file?.lastModified || 0) || null,
  mimeType: file?.type || "application/octet-stream",
});

const scanFileSystemHandle = async ({
  handle,
  rootPath = "",
  files,
  folders,
  fileMap,
  ctx,
}) => {
  const nextPath = normalizeSeparators([rootPath, handle.name].filter(Boolean).join("/"));

  if (handle.kind === "file") {
    const file = await handle.getFile();
    const fileEntry = createFileEntry({ relativePath: nextPath, file });
    if (!isSystemArtifact(fileEntry.relativePath)) {
      files.push(fileEntry);
      fileMap.set(fileEntry.clientEntryId, file);
    }
    await ctx.checkpoint({ fileCount: files.length, folderCount: folders.length });
    return;
  }

  folders.push(createFolderEntry(nextPath, handle.name));
  await ctx.checkpoint({ fileCount: files.length, folderCount: folders.length });

  let childCount = 0;
  for await (const child of handle.values()) {
    childCount += 1;
    await scanFileSystemHandle({
      handle: child,
      rootPath: nextPath,
      files,
      folders,
      fileMap,
      ctx,
    });
  }
  const folder = folders.find((entry) => entry.relativePath === nextPath);
  if (folder) folder.emptyFolder = childCount === 0;
};

const scanWebkitEntry = async ({
  entry,
  rootPath = "",
  files,
  folders,
  fileMap,
  ctx,
}) => {
  const nextPath = normalizeSeparators([rootPath, entry.name].filter(Boolean).join("/"));

  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    const fileEntry = createFileEntry({ relativePath: nextPath, file });
    if (!isSystemArtifact(fileEntry.relativePath)) {
      files.push(fileEntry);
      fileMap.set(fileEntry.clientEntryId, file);
    }
    await ctx.checkpoint({ fileCount: files.length, folderCount: folders.length });
    return;
  }

  folders.push(createFolderEntry(nextPath, entry.name));
  await ctx.checkpoint({ fileCount: files.length, folderCount: folders.length });

  const reader = entry.createReader();
  let childCount = 0;
  while (true) {
    const chunk = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    if (!chunk?.length) break;
    for (const child of chunk) {
      childCount += 1;
      await scanWebkitEntry({
        entry: child,
        rootPath: nextPath,
        files,
        folders,
        fileMap,
        ctx,
      });
    }
  }
  const folder = folders.find((entry) => entry.relativePath === nextPath);
  if (folder) folder.emptyFolder = childCount === 0;
};

const scanDataTransferItems = async (items, options = {}) => {
  const fileMap = new Map();
  const files = [];
  const folders = [];
  const ctx = createScanContext(options);

  for (const item of asArray(items)) {
    if (item.kind !== "file") continue;

    if (typeof item.getAsFileSystemHandle === "function") {
      const handle = await item.getAsFileSystemHandle();
      if (handle) {
        await scanFileSystemHandle({ handle, files, folders, fileMap, ctx });
        continue;
      }
    }

    if (typeof item.webkitGetAsEntry === "function") {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        await scanWebkitEntry({ entry, files, folders, fileMap, ctx });
        continue;
      }
    }

    const file = item.getAsFile?.();
    if (file) {
      const fileEntry = createFileEntry({ relativePath: file.name, file });
      if (!isSystemArtifact(fileEntry.relativePath)) {
        files.push(fileEntry);
        fileMap.set(fileEntry.clientEntryId, file);
      }
      await ctx.checkpoint({ fileCount: files.length, folderCount: folders.length });
    }
  }

  ctx.flushProgress();
  return { files, folders, fileMap };
};

export const scanDirectoryInput = async (fileList, options = {}) => {
  const files = [];
  const foldersByPath = new Map();
  const fileMap = new Map();
  const ctx = createScanContext(options);

  for (const file of asArray(fileList)) {
    const relativePath = normalizeSeparators(file.webkitRelativePath || file.name);
    const entry = createFileEntry({ relativePath, file });
    if (isSystemArtifact(entry.relativePath)) {
      await ctx.checkpoint({ fileCount: files.length, folderCount: foldersByPath.size });
      continue;
    }

    fileMap.set(entry.clientEntryId, file);
    files.push(entry);
    const segments = relativePath.split("/").filter(Boolean);
    let running = "";
    for (let index = 0; index < segments.length - 1; index += 1) {
      running = [running, segments[index]].filter(Boolean).join("/");
      if (!foldersByPath.has(running)) {
        foldersByPath.set(running, createFolderEntry(running, segments[index]));
      }
      foldersByPath.get(running).emptyFolder = false;
    }
    await ctx.checkpoint({ fileCount: files.length, folderCount: foldersByPath.size });
  }

  ctx.flushProgress();
  return {
    files,
    folders: Array.from(foldersByPath.values()),
    fileMap,
  };
};

export const scanDropPayload = async ({ dataTransfer, signal, onProgress } = {}) => {
  if (dataTransfer?.items?.length) {
    return scanDataTransferItems(dataTransfer.items, { signal, onProgress });
  }
  return scanDirectoryInput(dataTransfer?.files || [], { signal, onProgress });
};

export const summarizeImportSelection = ({ files = [], folders = [] } = {}) => {
  const totalBytes = files.reduce((sum, file) => sum + Number(file.size || 0), 0);
  const largestFile = files.reduce((largest, file) => {
    if (!largest) return file;
    return Number(file.size || 0) > Number(largest.size || 0) ? file : largest;
  }, null);
  const duplicatePathCount = files.length - new Set(files.map((file) => normalizeSeparators(file.relativePath).toLowerCase())).size;
  const unsupportedFiles = files.filter((file) => !file.fileName || !file.relativePath);
  const emptyFolders = folders.filter((folder) => folder.emptyFolder);

  return {
    totalFiles: files.length,
    totalFolders: folders.length,
    totalBytes,
    largestFile,
    duplicatePathCount,
    unsupportedFiles,
    emptyFolders,
  };
};

export const batchManifestEntries = (entries = [], batchSize = 400) => {
  const batches = [];
  for (let index = 0; index < entries.length; index += batchSize) {
    batches.push(entries.slice(index, index + batchSize));
  }
  return batches;
};

export const createManifestFromScan = ({ files = [], folders = [] } = {}) => [
  ...folders,
  ...files,
];
